/**
 * Match Validation Page
 * 
 * Temporary page for validating the match algorithm.
 * Two views:
 * 1. SUPPLY VIEW: "I have solo ice, who could come to me?"
 * 2. DEMAND VIEW: "I'm looking for scrimmages, who has solo ice I could go to?"
 * 
 * FOCUS MODE: Currently locked to Squirt B1 only until algorithm is perfected.
 */

import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

// =============================================================================
// FOCUS MODE: Squirt B1 Only
// =============================================================================
const FOCUS_LEVEL = 'boys_squirt_10u'
const FOCUS_LEVEL_DETAIL = 'B1'

export default function MatchValidation() {
  const [viewMode, setViewMode] = useState('demand') // 'supply' or 'demand'
  const [teams, setTeams] = useState([])
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [opportunities, setOpportunities] = useState([])
  const [postings, setPostings] = useState([])
  const [selectedPosting, setSelectedPosting] = useState(null)
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState(null)
  const [feedbackStats, setFeedbackStats] = useState({ good: 0, bad: 0, uncertain: 0, total: 0 })
  // Pre-set filters to Squirt B1
  const [filters, setFilters] = useState({
    teamLevel: FOCUS_LEVEL,
    levelDetail: FOCUS_LEVEL_DETAIL,
    teamSearch: ''
  })

  // Load data on mount and filter change
  useEffect(() => {
    if (viewMode === 'supply') {
      loadPostings()
    } else {
      loadTeams()
    }
    loadFeedbackStats()
  }, [filters, viewMode])

  const loadTeams = async () => {
    let query = supabase
      .from('teams')
      .select('id, name, display_name, association_name, city, team_level, level_detail, latitude, longitude')
      .not('association_name', 'is', null)
      .order('association_name', { ascending: true })

    if (filters.teamLevel !== 'all') {
      query = query.eq('team_level', filters.teamLevel)
    }
    if (filters.levelDetail !== 'all') {
      query = query.eq('level_detail', filters.levelDetail)
    }
    if (filters.teamSearch) {
      // Search by team name, display name, or association name
      query = query.or(`name.ilike.%${filters.teamSearch}%,display_name.ilike.%${filters.teamSearch}%,association_name.ilike.%${filters.teamSearch}%`)
    }

    const { data, error } = await query.limit(200)

    if (error) {
      console.error('Error loading teams:', error)
      return
    }

    setTeams(data || [])
  }

  // Sync calendars for selected team(s) via Edge Function
  const syncCalendars = async (teamIds) => {
    setSyncing(true)
    setSyncStatus({ message: `Starting calendar sync for ${teamIds.length} team(s)...`, type: 'info' })

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setSyncStatus({ message: 'Not authenticated. Please log in.', type: 'error' })
        setSyncing(false)
        return
      }

      // Call the Edge Function using supabase.functions.invoke (handles auth automatically)
      const { data, error } = await supabase.functions.invoke('seed-teams-sync', {
        body: {
          team_ids: teamIds,
          force: true
        }
      })

      if (error) {
        console.error('Sync error:', error)
        // Check if it's a CORS/network error vs actual function error
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          setSyncStatus({ 
            message: `⚠️ Network/CORS error. Try running locally: npx supabase functions serve seed-teams-sync --env-file .env.local`, 
            type: 'error' 
          })
        } else if (error.message?.includes('401') || error.message?.includes('403')) {
          setSyncStatus({ 
            message: `⚠️ Auth error. Make sure ADMIN_EMAIL_ALLOWLIST includes your email in Supabase secrets.`, 
            type: 'error' 
          })
        } else {
          setSyncStatus({ 
            message: `Sync failed: ${error.message || 'Unknown error'}`, 
            type: 'error' 
          })
        }
      } else if (data) {
        // Check if data is an error response
        if (data.error) {
          setSyncStatus({ 
            message: `Sync failed: ${data.error}. ${data.details || ''}`, 
            type: 'error' 
          })
        } else {
          setSyncStatus({ 
            message: `✅ Synced ${data.synced || 0} teams, ${data.new_events || 0} events, ${data.auto_postings_created || 0} postings created`, 
            type: 'success' 
          })
          // Reload data after sync
          if (selectedTeam) {
            loadOpportunitiesForTeam(selectedTeam)
          }
        }
      } else {
        setSyncStatus({ 
          message: 'Sync completed but no data returned', 
          type: 'success' 
        })
      }
    } catch (error) {
      console.error('Sync catch error:', error)
      setSyncStatus({ 
        message: `Sync error: ${error.message}. Check browser console for details.`, 
        type: 'error' 
      })
    }

    setSyncing(false)
  }

  // Fetch calendar events for a team on a specific date (Central Time)
  const fetchCalendarEventsForDate = async (teamId, date) => {
    // Minnesota is Central Time (UTC-6 in winter, UTC-5 in summer)
    // To find all events on a local date, we need to query a wider UTC range:
    // - Local midnight = UTC 06:00 (CST) or 05:00 (CDT)
    // - Local 11:59pm = UTC 05:59 next day (CST) or 04:59 (CDT)
    // We'll use UTC-6 (CST) as the baseline and add buffer for safety
    
    // Start: local date at midnight = ~06:00 UTC same day
    // End: local date at 11:59pm = ~06:00 UTC next day
    const startUTC = `${date}T06:00:00Z`
    
    // Calculate next day for end range
    const nextDay = new Date(date)
    nextDay.setDate(nextDay.getDate() + 1)
    const nextDayStr = nextDay.toISOString().split('T')[0]
    const endUTC = `${nextDayStr}T06:00:00Z`
    
    const { data, error } = await supabase
      .from('calendar_events')
      .select('id, title, start_time, end_time, event_type, location')
      .eq('team_id', teamId)
      .gte('start_time', startUTC)
      .lt('start_time', endUTC)
      .order('start_time', { ascending: true })

    if (error) {
      console.error('Error fetching calendar events:', error)
      return []
    }
    return data || []
  }

  const loadOpportunitiesForTeam = async (team) => {
    setLoading(true)
    setSelectedTeam(team)
    setOpportunities([])

    const allOpportunities = []

    // ========================================
    // PART 1: AWAY OPPORTUNITIES
    // Find other teams' solo ice where this team could go
    // ========================================
    const { data: awayPostings, error: awayError } = await supabase
      .from('scrimmage_postings')
      .select(`
        id,
        date,
        start_time,
        end_time,
        team_level,
        level_detail,
        rink_name,
        city,
        status,
        confidence_score,
        team_id,
        teams!inner(
          id,
          name,
          display_name,
          association_name,
          city,
          latitude,
          longitude
        )
      `)
      .eq('status', 'active')
      .eq('team_level', team.team_level)
      .eq('level_detail', team.level_detail)
      .neq('team_id', team.id) // Exclude own team's postings
      .gte('date', new Date().toISOString().split('T')[0]) // Future dates only
      .order('date', { ascending: true })
      .limit(50)

    if (awayError) {
      console.error('Error loading away postings:', awayError)
    } else {
      // Score each away opportunity
      for (const posting of awayPostings || []) {
        const { data: scoreData } = await supabase.rpc('get_combined_match_score', {
          p_posting_id: posting.id,
          p_demand_team_id: team.id
        })

        if (scoreData && scoreData.length > 0 && scoreData[0].combined_score !== null) {
          // Fetch YOUR team's calendar events for that date (to verify schedule fit)
          const yourEvents = await fetchCalendarEventsForDate(team.id, posting.date)
          
          allOpportunities.push({
            type: 'away',
            posting,
            score: scoreData[0],
            hostTeam: posting.teams,
            guestTeam: team,
            yourCalendarEvents: yourEvents
          })
        }
      }
    }

    // ========================================
    // PART 2: HOME OPPORTUNITIES
    // Find this team's own solo ice and who could come
    // ========================================
    const { data: homePostings, error: homeError } = await supabase
      .from('scrimmage_postings')
      .select(`
        id,
        date,
        start_time,
        end_time,
        team_level,
        level_detail,
        rink_name,
        city,
        status,
        confidence_score,
        team_id
      `)
      .eq('status', 'active')
      .eq('team_id', team.id) // Only this team's postings
      .gte('date', new Date().toISOString().split('T')[0])
      .order('date', { ascending: true })
      .limit(20)

    if (homeError) {
      console.error('Error loading home postings:', homeError)
    } else {
      // For each home posting, find potential guests
      for (const posting of homePostings || []) {
        // Get matches for this posting using the validation RPC
        const { data: matchData, error: matchError } = await supabase.rpc('get_posting_matches_for_validation', {
          p_posting_id: posting.id
        })

        if (matchError) {
          console.error(`Error getting matches for posting ${posting.id}:`, matchError)
          continue
        }

        // Add top 10 matches for each home posting (increased from 5 to catch more opportunities)
        const topMatches = (matchData || [])
          .sort((a, b) => b.combined_score - a.combined_score)
          .slice(0, 10)

        for (const match of topMatches) {
          // Fetch the GUEST team's calendar events for that date (to verify their schedule)
          const guestEvents = await fetchCalendarEventsForDate(match.demand_team_id, posting.date)
          
          allOpportunities.push({
            type: 'home',
            posting: {
              ...posting,
              teams: team // The posting team is the selected team
            },
            score: {
              combined_score: match.combined_score,
              solo_confidence_score: match.solo_confidence_score,
              schedule_fit_score: match.schedule_fit_score,
              distance_score: match.distance_score,
              distance_miles: match.distance_miles,
              penalty_applied: match.penalty_applied,
              same_association: match.same_association,
              same_district: match.same_district,
              posting_district: match.posting_district,
              demand_district: match.demand_district,
              match_reason: match.match_reason
            },
            hostTeam: team,
            guestTeam: {
              id: match.demand_team_id,
              name: match.demand_team_name,
              association_name: match.demand_association,
              city: match.demand_city
            },
            theirCalendarEvents: guestEvents
          })
        }
      }
    }

    // Sort all opportunities by fit score (highest first), then by date
    allOpportunities.sort((a, b) => {
      const scoreCompare = b.score.combined_score - a.score.combined_score
      if (scoreCompare !== 0) return scoreCompare
      return new Date(a.posting.date) - new Date(b.posting.date)
    })

    setOpportunities(allOpportunities)
    setLoading(false)
  }

  const loadPostings = async () => {
    let query = supabase
      .from('scrimmage_postings')
      .select(`
        id,
        date,
        start_time,
        end_time,
        team_level,
        level_detail,
        rink_name,
        city,
        status,
        teams!inner(
          id,
          name,
          association_name,
          city
        )
      `)
      .eq('status', 'active')
      .order('date', { ascending: true })
      .limit(50)

    if (filters.teamLevel !== 'all') {
      query = query.eq('team_level', filters.teamLevel)
    }
    if (filters.levelDetail !== 'all') {
      query = query.eq('level_detail', filters.levelDetail)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error loading postings:', error)
      return
    }

    // Apply team search filter client-side (since it's on joined table)
    let filteredData = data || []
    if (filters.teamSearch) {
      const searchLower = filters.teamSearch.toLowerCase()
      filteredData = filteredData.filter(p => 
        p.teams?.name?.toLowerCase().includes(searchLower) ||
        p.teams?.association_name?.toLowerCase().includes(searchLower)
      )
    }

    setPostings(filteredData)
  }

  const loadFeedbackStats = async () => {
    const { data, error } = await supabase
      .from('match_feedback')
      .select('rating')

    if (error) return

    const stats = { good: 0, bad: 0, uncertain: 0, total: data?.length || 0 }
    data?.forEach(f => {
      if (f.rating) stats[f.rating]++
    })
    setFeedbackStats(stats)
  }

  const loadMatches = async (posting) => {
    setLoading(true)
    setSelectedPosting(posting)

    // Use RPC to get matches with full scoring
    const { data, error } = await supabase.rpc('get_posting_matches_for_validation', {
      p_posting_id: posting.id
    })

    if (error) {
      console.error('Error loading matches:', error)
      setLoading(false)
      return
    }

    // Sort by combined_score descending
    const sorted = (data || []).sort((a, b) => b.combined_score - a.combined_score)
    setMatches(sorted)
    setLoading(false)
  }

  const submitFeedback = async (match, rating, reason, notes) => {
    const feedbackData = {
      posting_id: selectedPosting.id,
      posting_team_id: selectedPosting.teams.id,
      demand_team_id: match.demand_team_id,
      posting_date: selectedPosting.date,
      posting_team_name: selectedPosting.teams.name,
      posting_association: selectedPosting.teams.association_name,
      posting_city: selectedPosting.city,
      demand_team_name: match.demand_team_name,
      demand_association: match.demand_association,
      demand_city: match.demand_city,
      team_level: selectedPosting.team_level,
      level_detail: selectedPosting.level_detail,
      combined_score: match.combined_score,
      solo_confidence_score: match.solo_confidence_score,
      schedule_fit_score: match.schedule_fit_score,
      distance_score: match.distance_score,
      distance_miles: match.distance_miles,
      penalty_applied: match.penalty_applied,
      same_association: match.same_association,
      same_district: match.same_district,
      posting_district: match.posting_district,
      demand_district: match.demand_district,
      match_reason: match.match_reason,
      rating,
      feedback_reason: reason,
      feedback_notes: notes
    }

    const { error } = await supabase
      .from('match_feedback')
      .insert(feedbackData)

    if (error) {
      console.error('Error submitting feedback:', error)
      alert('Failed to submit feedback')
      return
    }

    // Update local state
    setMatches(prev => prev.map(m => 
      m.demand_team_id === match.demand_team_id 
        ? { ...m, feedbackSubmitted: true, userRating: rating }
        : m
    ))
    loadFeedbackStats()
  }

  const formatLevel = (level, detail) => {
    const levelLabels = {
      'boys_bantam_14u': 'Bantam',
      'boys_peewee_12u': 'Peewee',
      'boys_squirt_10u': 'Squirt',
      'girls_15u': 'Girls 15U',
      'girls_12u': 'Girls 12U',
      'girls_10u': 'Girls 10U'
    }
    return `${levelLabels[level] || level} ${detail || ''}`
  }

  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    const [h, m] = timeStr.split(':')
    const hour = parseInt(h)
    const ampm = hour >= 12 ? 'pm' : 'am'
    const hour12 = hour % 12 || 12
    return `${hour12}:${m}${ampm}`
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Match Algorithm Validation</h1>
          <p className="text-gray-600 mt-1">
            Review match results and provide feedback to improve the algorithm.
          </p>
        </div>

        {/* View Mode Toggle */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">View Mode</h2>
          <div className="flex gap-4">
            <button
              onClick={() => setViewMode('demand')}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === 'demand'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔍 Find Scrimmages (Demand)
              <div className="text-xs font-normal mt-0.5">
                "I'm looking for ice time to go to"
              </div>
            </button>
            <button
              onClick={() => setViewMode('supply')}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === 'supply'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🏒 Offer Solo Ice (Supply)
              <div className="text-xs font-normal mt-0.5">
                "I have solo ice, who could come to me?"
              </div>
            </button>
          </div>
        </div>

        {/* Feedback Stats */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">Feedback Summary</h2>
          <div className="flex gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{feedbackStats.good}</div>
              <div className="text-sm text-gray-500">Good</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{feedbackStats.bad}</div>
              <div className="text-sm text-gray-500">Bad</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{feedbackStats.uncertain}</div>
              <div className="text-sm text-gray-500">Uncertain</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{feedbackStats.total}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">Filters</h2>
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Search Team/Association</label>
              <input
                type="text"
                value={filters.teamSearch}
                onChange={(e) => setFilters(prev => ({ ...prev, teamSearch: e.target.value }))}
                placeholder="e.g., CCHA, Centennial..."
                className="border rounded px-3 py-2 w-64"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Level</label>
              <select
                value={filters.teamLevel}
                onChange={(e) => setFilters(prev => ({ ...prev, teamLevel: e.target.value }))}
                className="border rounded px-3 py-2"
              >
                <option value="all">All Levels</option>
                <option value="boys_squirt_10u">Squirt (Boys 10U)</option>
                <option value="boys_peewee_12u">Peewee (Boys 12U)</option>
                <option value="boys_bantam_14u">Bantam (Boys 14U)</option>
                <option value="girls_10u">Girls 10U</option>
                <option value="girls_12u">Girls 12U</option>
                <option value="girls_15u">Girls 15U</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tier</label>
              <select
                value={filters.levelDetail}
                onChange={(e) => setFilters(prev => ({ ...prev, levelDetail: e.target.value }))}
                className="border rounded px-3 py-2"
              >
                <option value="all">All Tiers</option>
                <option value="AA">AA</option>
                <option value="A">A</option>
                <option value="B1">B1</option>
                <option value="B2">B2</option>
                <option value="C">C</option>
              </select>
            </div>
            {filters.teamSearch && (
              <div className="flex items-end">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, teamSearch: '' }))}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded"
                >
                  Clear Search
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Admin Sync Section */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 border-2 border-orange-200">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            ⚙️ Admin: Calendar Sync
          </h2>
          <div className="flex flex-wrap gap-4 items-start">
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-2">
                Sync calendars to fetch latest solo ice times and create postings.
                {teams.length > 0 && (
                  <span className="ml-2 font-medium text-gray-800">
                    ({teams.length} teams in current filter)
                  </span>
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedTeam && (
                  <button
                    onClick={() => syncCalendars([selectedTeam.id])}
                    disabled={syncing}
                    className="px-4 py-2 bg-orange-600 text-white rounded font-medium hover:bg-orange-700 disabled:opacity-50"
                  >
                    {syncing ? '⏳ Syncing...' : `🔄 Sync ${selectedTeam.display_name || selectedTeam.name}`}
                  </button>
                )}
                <button
                  onClick={() => {
                    const teamIds = teams.map(t => t.id)
                    if (teamIds.length > 0) {
                      if (teamIds.length > 20 && !confirm(`Sync all ${teamIds.length} teams? This may take a few minutes.`)) {
                        return
                      }
                      syncCalendars(teamIds)
                    }
                  }}
                  disabled={syncing || teams.length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {syncing ? '⏳ Syncing...' : `🔄 Sync All ${teams.length} Filtered Teams`}
                </button>
              </div>
            </div>
            {syncStatus && (
              <div className={`flex-1 p-3 rounded text-sm ${
                syncStatus.type === 'success' ? 'bg-green-100 text-green-800' :
                syncStatus.type === 'error' ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {syncStatus.message}
              </div>
            )}
          </div>
        </div>

        {/* DEMAND VIEW: Select team, see opportunities */}
        {viewMode === 'demand' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Team Selection */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-blue-50">
                <h2 className="text-lg font-semibold">Select Your Team ({teams.length})</h2>
                <p className="text-xs text-gray-600 mt-1">Choose a team to find scrimmage opportunities</p>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {teams.map(team => (
                  <div
                    key={team.id}
                    onClick={() => loadOpportunitiesForTeam(team)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                      selectedTeam?.id === team.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="font-medium">{team.display_name || team.name}</div>
                    <div className="text-sm text-gray-600">{team.association_name}</div>
                    <div className="text-sm text-gray-500">{team.city}</div>
                  </div>
                ))}
                {teams.length === 0 && (
                  <div className="p-4 text-gray-500 text-center">No teams found. Select level/tier filters.</div>
                )}
              </div>
            </div>

            {/* Opportunities for Selected Team */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-green-50">
                <h2 className="text-lg font-semibold">
                  {selectedTeam 
                    ? `Scrimmage Opportunities for ${selectedTeam.display_name || selectedTeam.name}`
                    : 'Select a team to find opportunities'}
                </h2>
                {selectedTeam && (
                  <div className="text-sm text-gray-600 mt-1 flex gap-4">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-blue-500 rounded"></span>
                      AWAY: Ice you can go to
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-green-500 rounded"></span>
                      HOME: Your ice, invite them
                    </span>
                  </div>
                )}
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-gray-500">Calculating match scores...</div>
              ) : opportunities.length > 0 ? (
                <div className="max-h-[600px] overflow-y-auto">
                  {opportunities.map((opp, idx) => (
                    <OpportunityCard
                      key={`${opp.posting.id}-${opp.guestTeam?.id || opp.hostTeam?.id}-${idx}`}
                      opportunity={opp}
                      rank={idx + 1}
                      demandTeam={selectedTeam}
                    />
                  ))}
                </div>
              ) : selectedTeam ? (
                <div className="p-8 text-center text-gray-500">
                  No opportunities found. This could mean:
                  <ul className="mt-2 text-sm list-disc list-inside">
                    <li>No teams at this level have posted solo ice</li>
                    <li>{selectedTeam.display_name || selectedTeam.name} has games/tournaments on those days</li>
                    <li>All opportunities were hard-blocked (distance, gender, etc.)</li>
                  </ul>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  Select a team from the left panel to find scrimmage opportunities
                </div>
              )}
            </div>
          </div>
        )}

        {/* SUPPLY VIEW: Select posting, see who could come */}
        {viewMode === 'supply' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Postings List */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-green-50">
                <h2 className="text-lg font-semibold">Solo Ice Postings ({postings.length})</h2>
                <p className="text-xs text-gray-600 mt-1">Select to see who could come for a scrimmage</p>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {postings.map(posting => (
                  <div
                    key={posting.id}
                    onClick={() => loadMatches(posting)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                      selectedPosting?.id === posting.id ? 'bg-green-50 border-l-4 border-l-green-500' : ''
                    }`}
                  >
                    <div className="font-medium">{posting.teams?.association_name || posting.teams?.name}</div>
                    <div className="text-sm text-gray-600">
                      {posting.teams?.name} • {formatLevel(posting.team_level, posting.level_detail)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(posting.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} @ {formatTime(posting.start_time)} • {posting.rink_name || posting.city}
                    </div>
                  </div>
                ))}
                {postings.length === 0 && (
                  <div className="p-4 text-gray-500 text-center">No postings found</div>
                )}
              </div>
            </div>

            {/* Match Results */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-green-50">
                <h2 className="text-lg font-semibold">
                  {selectedPosting 
                    ? `Who could come to ${selectedPosting.teams?.association_name}'s solo ice?`
                    : 'Select a posting to view matches'}
                </h2>
                {selectedPosting && (
                  <div className="text-sm text-gray-600 mt-1">
                    {selectedPosting.teams?.name} • {formatLevel(selectedPosting.team_level, selectedPosting.level_detail)} • 
                    {new Date(selectedPosting.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} @ {formatTime(selectedPosting.start_time)} • 
                    {selectedPosting.rink_name || selectedPosting.city}
                  </div>
                )}
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading matches...</div>
              ) : matches.length > 0 ? (
                <div className="max-h-[600px] overflow-y-auto">
                  {matches.map((match, idx) => (
                    <MatchCard
                      key={match.demand_team_id}
                      match={match}
                      rank={idx + 1}
                      onFeedback={submitFeedback}
                    />
                  ))}
                </div>
              ) : selectedPosting ? (
                <div className="p-8 text-center text-gray-500">No matches found</div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  Select a posting from the left panel to view potential matches
                </div>
              )}
            </div>
          </div>
        )}

        {/* Algorithm Documentation */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Algorithm Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h3 className="font-medium mb-2">Scoring Formula</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li><strong>Solo Ice Confidence (40%)</strong> - How confident our calendar parser is that this is actually available solo ice (vs shared practice, game, etc.)</li>
                <li><strong>Schedule Fit (40%)</strong> - Based on the potential guest team's calendar conflicts. Games/tournaments = blocked. Practices reduce score based on gap (4+ hrs = fine)</li>
                <li><strong>Distance (20%)</strong> - Proximity scoring (90mi cap)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2">Penalties</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li><strong>Same Association: -25 points</strong> - Teams unlikely to want internal scrimmage</li>
                <li><strong>Same District: -20 points</strong> - Teams likely play in regular season</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2">Hard Blocks</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li>Cross-gender matches (boys vs girls)</li>
                <li>Different age levels (e.g., Squirt vs Peewee)</li>
                <li>Different tier levels (e.g., A vs B1)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2">District Estimation</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li>D1 - Northeast Metro (Blaine, White Bear Lake)</li>
                <li>D2 - South Metro (Eagan, Lakeville)</li>
                <li>D3 - Southwest Metro (Minnetonka, Chaska)</li>
                <li>D4 - Central Metro (Edina, Bloomington)</li>
                <li>D5 - Northwest Metro (Rogers, Elk River)</li>
                <li>D6 - West Metro (Wayzata, Plymouth)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// OpportunityCard: Shows a scrimmage opportunity - both HOME and AWAY
function OpportunityCard({ opportunity, rank, demandTeam }) {
  const { posting, score, type, hostTeam, guestTeam, yourCalendarEvents, theirCalendarEvents } = opportunity
  const isHome = type === 'home'
  
  // Get the relevant calendar events based on view type
  const calendarEvents = isHome ? theirCalendarEvents : yourCalendarEvents
  const calendarLabel = isHome ? `${guestTeam?.display_name || guestTeam?.name}'s Schedule` : 'Your Schedule'
  
  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    const [h, m] = timeStr.split(':')
    const hour = parseInt(h)
    const ampm = hour >= 12 ? 'pm' : 'am'
    const hour12 = hour % 12 || 12
    return `${hour12}:${m}${ampm}`
  }

  const scoreColor = score.combined_score >= 75 ? 'text-green-600' :
                     score.combined_score >= 60 ? 'text-yellow-600' : 'text-red-600'

  // For HOME: we're the host, guestTeam is who could come
  // For AWAY: hostTeam is who has the ice, we're the guest
  const otherTeam = isHome ? guestTeam : hostTeam

  return (
    <div className={`p-4 border-b hover:bg-gray-50 ${isHome ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-blue-500'}`}>
      {/* HOME/AWAY Badge */}
      <div className="flex items-center gap-2 mb-2">
        {isHome ? (
          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
            🏠 HOME - Invite them to your ice
          </span>
        ) : (
          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
            ✈️ AWAY - Go to their ice
          </span>
        )}
      </div>

      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-400">#{rank}</span>
            <span className="font-medium">{otherTeam?.association_name || otherTeam?.display_name || otherTeam?.name}</span>
          </div>
          <div className="text-sm text-gray-600">{otherTeam?.display_name || otherTeam?.name}</div>
          <div className="text-sm text-gray-500">
            📅 {new Date(posting.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} @ {formatTime(posting.start_time)}
          </div>
          <div className="text-sm text-gray-500">
            📍 {posting.rink_name || posting.city} • {score.distance_miles?.toFixed(1)} mi {isHome ? 'from them' : 'away'}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${scoreColor}`}>{score.combined_score}</div>
          <div className="text-xs text-gray-500">fit score</div>
        </div>
      </div>

      {/* Score Breakdown */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
        <div className="bg-gray-100 rounded p-2 text-center" title="How confident we are this is actually available solo ice (from calendar parsing)">
          <div className="font-medium">{score.solo_confidence_score}</div>
          <div className="text-gray-500">Solo Ice Confidence</div>
        </div>
        <div className="bg-gray-100 rounded p-2 text-center">
          <div className="font-medium">{score.schedule_fit_score}</div>
          <div className="text-gray-500">{isHome ? 'Their Sched' : 'Your Sched'}</div>
        </div>
        <div className="bg-gray-100 rounded p-2 text-center">
          <div className="font-medium">{score.distance_score}</div>
          <div className="text-gray-500">Distance</div>
        </div>
        <div className="bg-gray-100 rounded p-2 text-center">
          <div className={`font-medium ${score.penalty_applied > 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {score.penalty_applied > 0 ? `-${score.penalty_applied}` : '0'}
          </div>
          <div className="text-gray-500">Penalty</div>
        </div>
      </div>

      {/* Flags */}
      <div className="mt-2 flex gap-2 flex-wrap">
        {score.same_association && (
          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
            Same Association (-25)
          </span>
        )}
        {score.same_district && (
          <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
            Same District: {score.demand_district} (-20)
          </span>
        )}
        {!score.same_association && !score.same_district && (
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
            Different District: {score.posting_district} → {score.demand_district}
          </span>
        )}
      </div>

      {/* Match Reason */}
      <div className="mt-2 text-xs text-gray-500">{score.match_reason}</div>

      {/* Calendar Events for Validation */}
      <div className="mt-3 p-2 bg-gray-50 rounded border">
        <div className="text-xs font-medium text-gray-700 mb-1">
          📋 {calendarLabel} on {new Date(posting.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}:
        </div>
        {calendarEvents && calendarEvents.length > 0 ? (
          <div className="space-y-1">
            {calendarEvents.map((evt, idx) => (
              <div key={idx} className="text-xs flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded ${
                  evt.event_type === 'game' ? 'bg-red-100 text-red-700' :
                  evt.event_type === 'practice' ? 'bg-blue-100 text-blue-700' :
                  evt.event_type === 'tournament' ? 'bg-purple-100 text-purple-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {evt.event_type}
                </span>
                <span className="text-gray-600">
                  {new Date(evt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  {evt.end_time && ` - ${new Date(evt.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                </span>
                <span className="text-gray-500 truncate">{evt.title}</span>
                {evt.location && <span className="text-gray-400">@ {evt.location}</span>}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500 italic">
            No calendar events synced for this date
          </div>
        )}
      </div>

      {/* Action hint */}
      <div className="mt-3 text-sm">
        {isHome ? (
          <span className="text-green-700">
            💡 Invite {otherTeam?.association_name} to come to your {formatTime(posting.start_time)} solo ice
          </span>
        ) : (
          <span className="text-blue-700">
            💡 Reach out to {otherTeam?.association_name} about attending their scrimmage
          </span>
        )}
      </div>
    </div>
  )
}

function MatchCard({ match, rank, onFeedback }) {
  const [showFeedback, setShowFeedback] = useState(false)
  const [rating, setRating] = useState('')
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!rating) return
    setSubmitting(true)
    await onFeedback(match, rating, reason, notes)
    setSubmitting(false)
    setShowFeedback(false)
  }

  const scoreColor = match.combined_score >= 75 ? 'text-green-600' :
                     match.combined_score >= 60 ? 'text-yellow-600' : 'text-red-600'

  const feedbackReasons = [
    'Teams actually in same association',
    'Teams NOT in same association (false positive)',
    'Teams in same district',
    'Teams NOT in same district (false positive)',
    'Good distance match',
    'Too far away',
    'Would never play each other',
    'Great match opportunity',
    'Other (see notes)'
  ]

  return (
    <div className={`p-4 border-b ${match.feedbackSubmitted ? 'bg-gray-50' : ''}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-400">#{rank}</span>
            <span className="font-medium">{match.demand_team_name}</span>
            {match.feedbackSubmitted && (
              <span className={`text-xs px-2 py-0.5 rounded ${
                match.userRating === 'good' ? 'bg-green-100 text-green-700' :
                match.userRating === 'bad' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {match.userRating}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600">{match.demand_association}</div>
          <div className="text-sm text-gray-500">{match.demand_city} • {match.distance_miles?.toFixed(1)} mi</div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${scoreColor}`}>{match.combined_score}</div>
          <div className="text-xs text-gray-500">combined</div>
        </div>
      </div>

      {/* Score Breakdown */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
        <div className="bg-gray-100 rounded p-2 text-center">
          <div className="font-medium">{match.solo_confidence_score}</div>
          <div className="text-gray-500">Solo</div>
        </div>
        <div className="bg-gray-100 rounded p-2 text-center">
          <div className="font-medium">{match.schedule_fit_score}</div>
          <div className="text-gray-500">Schedule</div>
        </div>
        <div className="bg-gray-100 rounded p-2 text-center">
          <div className="font-medium">{match.distance_score}</div>
          <div className="text-gray-500">Distance</div>
        </div>
        <div className="bg-gray-100 rounded p-2 text-center">
          <div className={`font-medium ${match.penalty_applied > 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {match.penalty_applied > 0 ? `-${match.penalty_applied}` : '0'}
          </div>
          <div className="text-gray-500">Penalty</div>
        </div>
      </div>

      {/* Penalty/Flag indicators */}
      <div className="mt-2 flex gap-2 flex-wrap">
        {match.same_association && (
          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
            Same Association (-25)
          </span>
        )}
        {match.same_district && (
          <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
            Same District: {match.demand_district} (-20)
          </span>
        )}
        {!match.same_association && !match.same_district && (
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
            Different District: {match.posting_district} → {match.demand_district}
          </span>
        )}
      </div>

      {/* Match Reason */}
      <div className="mt-2 text-xs text-gray-500">{match.match_reason}</div>

      {/* Feedback Section */}
      {!match.feedbackSubmitted && (
        <div className="mt-3">
          {!showFeedback ? (
            <button
              onClick={() => setShowFeedback(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + Add Feedback
            </button>
          ) : (
            <div className="bg-gray-50 rounded p-3 space-y-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setRating('good')}
                  className={`flex-1 py-2 rounded text-sm font-medium ${
                    rating === 'good' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  👍 Good Match
                </button>
                <button
                  onClick={() => setRating('bad')}
                  className={`flex-1 py-2 rounded text-sm font-medium ${
                    rating === 'bad' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  👎 Bad Match
                </button>
                <button
                  onClick={() => setRating('uncertain')}
                  className={`flex-1 py-2 rounded text-sm font-medium ${
                    rating === 'uncertain' 
                      ? 'bg-yellow-600 text-white' 
                      : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  }`}
                >
                  🤔 Uncertain
                </button>
              </div>
              
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="">Select reason...</option>
                {feedbackReasons.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes (optional)"
                className="w-full border rounded px-3 py-2 text-sm"
                rows={2}
              />

              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={!rating || submitting}
                  className="flex-1 bg-blue-600 text-white py-2 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
                <button
                  onClick={() => setShowFeedback(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
